/**
 * "What Can Start?" — driven by the canonical backend classification
 * (GET /execution-intelligence/projects/{id}/what-can-start, contract
 * "what-can-start-2"). The verdicts are backend-native — CAN_START_NOW /
 * CAN_START_CONDITIONALLY / BLOCKED / IN_PROGRESS / COMPLETE / UNKNOWN —
 * and the UI never reconstructs them from gate arithmetic.
 *
 * Each workface explains itself in construction language taken from the
 * gate engine's own sentences: governing constraint, blocking reasons,
 * conditions, missing facts, and per-gate evidence. Safe parallel work and
 * resequencing candidates are surfaced as the backend reports them.
 *
 * Includes Project Realism (C5) and the on-demand recalculation chain (C6):
 * recalculate refreshes derived intelligence — it never applies schedule
 * changes.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  classifyWorkflowError,
  executionIntelligenceApi,
  schedulingAgentApi,
} from "../../services/intelligenceService";
import { canEditSchedule } from "../../lib/roles";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import {
  SectionLoading,
  SectionEmpty,
  SectionError,
  FeatureDisabled,
} from "../../components/workflow/WorkflowStates";
import {
  STARTABILITY_DISPLAY,
  startabilityDisplay,
  FACET_LABELS,
  GATE_STATUS_STYLES,
  gateLabel,
} from "./readinessPresentation";
import RealismPanel, { RecalculateButton } from "./RealismPanel";
import FlowDirectorPanel from "../runtime/FlowDirectorPanel";
import { recalculationSentence } from "../precon/preconPresentation";
import {
  presentFrontTruth,
  frontHeadlineReality,
} from "../runtime/gateTruthPresentation";
import { PLATFORM_GAP_OWNER_UNKNOWN, PLATFORM_GAP_NOT_A_FAILURE } from "../runtime/runtimeVocabulary";
import { WHAT_CAN_START_CONTRACT } from "../runtime/gateTruthPresentation";
import { ContractMismatchCallout } from "../runtime/RuntimePrimitives";
import FieldCaptureSheet from "../runtime/FieldCaptureSheet";
import { fieldObservationApi } from "../../services/intelligenceService";
import {
  ChevronDown,
  RefreshCw,
  Loader2,
  Gauge,
  AlertTriangle,
  GitBranch,
  Layers,
  ClipboardCheck,
} from "lucide-react";

const BUCKET_ORDER = [
  "CAN_START_NOW",
  "CAN_START_CONDITIONALLY",
  "BLOCKED",
  "IN_PROGRESS",
  "COMPLETE",
  "UNKNOWN",
];

const FacetChip = ({ facetKey, facet }) => {
  if (!facet || facet.status === "NOT_REQUIRED") return null;
  return (
    <Badge
      variant="outline"
      title={(facet.details || []).join("\n") || undefined}
      className={`${GATE_STATUS_STYLES[facet.status] || GATE_STATUS_STYLES.UNKNOWN} font-normal text-[10px]`}
    >
      {FACET_LABELS[facetKey] || facetKey}: {facet.status}
    </Badge>
  );
};

/**
 * Lane tones. `unknown` is deliberately neither red nor green, `gap` is its
 * own colour because a platform gap is not a site problem, and `conflict` is
 * distinct from both — a disagreement is not a failure.
 */
const LANE_TONE = {
  failed: "text-red-400",
  attention: "text-amber-400",
  unknown: "text-muted-foreground",
  conflict: "text-violet-400",
  gap: "text-sky-400",
};

const InstanceRow = ({ instance, user }) => {
  const [open, setOpen] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const display = startabilityDisplay(instance.status);
  // Partitioned from the payload's own lanes; nothing here is recomputed.
  const truth = presentFrontTruth(instance);
  const frontReality = frontHeadlineReality(truth);

  const evaluate = async () => {
    setEvaluating(true);
    try {
      await schedulingAgentApi.evaluate(instance.instanceId);
      toast.success("Instance re-evaluated. Refresh to see the updated classification.");
    } catch (err) {
      const c = classifyWorkflowError(err);
      toast.error(c.kind === "disabled" ? "The readiness engine is not enabled here." : c.message);
    } finally {
      setEvaluating(false);
    }
  };

  const headline =
    instance.governingConstraint ||
    instance.blockingReasons?.[0] ||
    (instance.status === "CAN_START_CONDITIONALLY" ? instance.conditions?.[0] : null) ||
    (instance.status === "UNKNOWN" ? instance.missingFacts?.[0] : null);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg bg-muted/30 border border-border/50">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full text-left px-3 py-2.5 flex items-start justify-between gap-3 hover:bg-muted/40 rounded-lg"
          >
            <div className="min-w-0 space-y-0.5">
              <span className="text-sm font-medium">{instance.instanceName}</span>
              {headline && <p className="text-xs text-muted-foreground">{headline}</p>}
              {instance.earliestRealisticStart && (
                <p className="text-[10px] text-muted-foreground">
                  Earliest realistic start: {instance.earliestRealisticStart}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="outline" className={`${display.className} font-medium`}>
                <display.Icon className="w-3 h-3 mr-1" />
                {display.label}
              </Badge>
              {frontReality && frontReality.key !== "ESTABLISHED_POSITIVE" ? (
                <Badge
                  variant="outline"
                  className={`font-normal text-[10px] ${LANE_TONE[frontReality.tone] || LANE_TONE.unknown}`}
                  title={frontReality.meaning}
                  data-testid="front-reality"
                >
                  {frontReality.label}
                </Badge>
              ) : null}
              <ChevronDown
                className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
              />
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-3 pb-3 space-y-2">
            {/* Named facets */}
            <div className="flex flex-wrap gap-1">
              {Object.entries(instance.facets || {}).map(([key, facet]) => (
                <FacetChip key={key} facetKey={key} facet={facet} />
              ))}
            </div>

            {/* THE FIVE REALITIES, each from its own payload field.
                `what-can-start-2` moved reasons that were never established
                failures out of `blockingReasons` and into
                `notEstablishedReasons`; rendering only the old two lanes
                would show a refusal with no reason at all. */}
            {truth.lanes.map((lane) => (
              <div key={lane.key} className="space-y-0.5" data-testid={`lane-${lane.key}`}>
                <p className={`text-xs font-semibold ${LANE_TONE[lane.tone] || LANE_TONE.unknown}`}>
                  {lane.title}
                </p>
                <p className="text-[10px] text-muted-foreground leading-relaxed">{lane.lead}</p>
                {lane.items.map((item, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    {item}
                  </p>
                ))}
                {(lane.gates || []).map((g, i) => (
                  <div key={i} className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">
                      {g.gap ? g.gap.label : g.detail || g.gate}
                    </p>
                    {g.gap ? (
                      <>
                        <p className="text-[10px] text-muted-foreground leading-relaxed">
                          {g.gap.meaning}
                        </p>
                        {g.gap.needs ? (
                          <p className="text-[10px] text-muted-foreground">
                            What would establish it: {g.gap.needs}
                          </p>
                        ) : null}
                      </>
                    ) : null}
                    {g.conflict && g.detail ? (
                      <p className="text-[10px] text-muted-foreground leading-relaxed">{g.detail}</p>
                    ) : null}
                  </div>
                ))}
                {lane.key === "platformGap" ? (
                  <>
                    <p className="text-[10px] text-muted-foreground leading-relaxed" data-testid="gap-not-a-failure">
                      {PLATFORM_GAP_NOT_A_FAILURE}
                    </p>
                    <p className="text-[10px] text-muted-foreground leading-relaxed" data-testid="gap-owner-unknown">
                      {PLATFORM_GAP_OWNER_UNKNOWN}
                    </p>
                  </>
                ) : null}
              </div>
            ))}

            {truth.withheldWithoutReason && (
              <p className="text-xs text-muted-foreground" data-testid="withheld-no-reason">
                This work is not cleared to start and the runtime named no reason in any lane.
                That is worth reporting — it is not a sign the work is ready.
              </p>
            )}

            {!truth.cleared && (
              <p className="text-[10px] text-muted-foreground leading-relaxed" data-testid="withheld-note">
                {truth.withheldNote}
              </p>
            )}
            {(instance.missingFacts || []).length > 0 && (
              <div className="space-y-0.5">
                <p className="text-xs font-semibold text-muted-foreground">Missing facts</p>
                {instance.missingFacts.map((m, i) => (
                  <p key={i} className="text-xs text-muted-foreground">{m}</p>
                ))}
              </div>
            )}

            {/* Per-gate evidence */}
            {(instance.evidence || []).length > 0 && (
              <div className="space-y-0.5">
                <p className="text-xs font-semibold text-muted-foreground">Gate evidence</p>
                <div className="flex flex-wrap gap-1">
                  {instance.evidence.map((g, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      title={g.detail || undefined}
                      className={`${GATE_STATUS_STYLES[g.status] || GATE_STATUS_STYLES.UNKNOWN} font-normal text-[10px]`}
                    >
                      {gateLabel(g.gate)}: {g.status}
                      {g.hard ? " (hard)" : ""}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <span>
                Stored readiness: {instance.readinessState || "—"}
                {instance.readinessScore != null ? ` · score ${Math.round(instance.readinessScore)}` : ""}
              </span>
              {canEditSchedule(user) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-[10px] px-2 ml-auto"
                  onClick={evaluate}
                  disabled={evaluating}
                >
                  {evaluating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Evaluate instance
                </Button>
              )}
            </div>

            {/* Recording what happened is not the same act as evaluating or
                starting — the sheet keeps the fact and the command apart. */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-[11px] px-2"
                onClick={() => setCapturing(true)}
                data-testid="open-field-capture"
              >
                <ClipboardCheck className="w-3 h-3 mr-1" />
                Record what happened
              </Button>
            </div>
          </div>
        </CollapsibleContent>

        <FieldCaptureSheet
          open={capturing}
          onOpenChange={setCapturing}
          target="instance"
          targetId={instance.instanceId}
          targetName={instance.instanceName || instance.instanceId}
          onRecord={(body) => fieldObservationApi.recordInstance(instance.instanceId, body)}
          onStartWork={(opts) => fieldObservationApi.startWork(instance.instanceId, opts)}
        />
      </div>
    </Collapsible>
  );
};

const ReadinessTab = ({ projectId, user, lookahead }) => {
  const [view, setView] = useState(null);
  const [classified, setClassified] = useState(null);
  const [loading, setLoading] = useState(true);

  const [realism, setRealism] = useState(null);
  const [realismClassified, setRealismClassified] = useState(null);
  const [realismLoading, setRealismLoading] = useState(true);

  const [flow, setFlow] = useState(null);
  const [flowClassified, setFlowClassified] = useState(null);

  const [recalcBusy, setRecalcBusy] = useState(false);
  // Drops responses that arrive after the user switched projects (this tab
  // does not remount on a /projects/A → /projects/B param change).
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    setLoading(true);
    setClassified(null);
    setView(null);
    // Cleared here, with the others: leaving the previous flow read mounted
    // while the classification above it reloads would show two reads from
    // different moments side by side.
    setFlow(null);
    setFlowClassified(null);
    try {
      const data = await executionIntelligenceApi.whatCanStart(projectId);
      if (seq === seqRef.current) setView(data);
    } catch (err) {
      if (seq === seqRef.current) setClassified(classifyWorkflowError(err));
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
    if (seq !== seqRef.current) return;
    setRealismLoading(true);
    setRealismClassified(null);
    setRealism(null);
    try {
      const data = await executionIntelligenceApi.realism(projectId);
      if (seq === seqRef.current) setRealism(data);
    } catch (err) {
      if (seq === seqRef.current) {
        setRealism(null);
        setRealismClassified(classifyWorkflowError(err));
      }
    } finally {
      if (seq === seqRef.current) setRealismLoading(false);
    }
    if (seq !== seqRef.current) return;
    // The flow read is the same classification re-expressed, plus the
    // recorded-transition lane. It carries its own refusals — the
    // transition rows are layered behind a separate capability — and must
    // never be able to blank the classification above it.
    try {
      const data = await executionIntelligenceApi.projectFlow(projectId);
      if (seq === seqRef.current) setFlow(data);
    } catch (err) {
      if (seq === seqRef.current) setFlowClassified(classifyWorkflowError(err));
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const recalculate = async () => {
    setRecalcBusy(true);
    try {
      const summary = await executionIntelligenceApi.recalculate(projectId);
      if (summary.skipped) {
        toast.warning(summary.reason || "Recalculation was skipped.");
      } else {
        // Payload-backed: see the note in ControlPlaneTab. The baseline
        // sentence comes from `approvedBaselineChanged`, and an absent key
        // is neither a yes nor a no.
        toast.success(recalculationSentence(summary));
      }
      await load();
    } catch (err) {
      const c = classifyWorkflowError(err);
      toast.error(c.kind === "disabled" ? "Execution intelligence is not enabled here." : c.message);
    } finally {
      setRecalcBusy(false);
    }
  };

  const buckets = useMemo(() => {
    const grouped = Object.fromEntries(BUCKET_ORDER.map((k) => [k, []]));
    (view?.instances || []).forEach((inst) => {
      const key = STARTABILITY_DISPLAY[inst.status] ? inst.status : "UNKNOWN";
      grouped[key].push(inst);
    });
    return grouped;
  }, [view]);

  const nameById = useMemo(() => {
    const map = {};
    (view?.instances || []).forEach((i) => {
      map[i.instanceId] = i.instanceName;
    });
    return map;
  }, [view]);

  if (loading) return <SectionLoading label="Asking the system what can start…" />;

  if (classified?.kind === "disabled") {
    // Honest fallback: dependency-only lookahead.
    if (lookahead?.failed) {
      return (
        <FeatureDisabled
          feature="Execution intelligence"
          hint="Gate-level readiness is not enabled here, and the dependency-only lookahead also failed to load — readiness is unknown right now. Reload the page to retry."
        />
      );
    }
    const items = lookahead?.items || [];
    const ready = items.filter((i) => i.lookahead_status === "ready");
    const blocked = items.filter((i) => i.lookahead_status === "blocked");
    return (
      <div className="space-y-4">
        <FeatureDisabled
          feature="Execution intelligence"
          hint="The canonical What Can Start classification is built but not enabled here. Below is the dependency-only view from the lookahead — it knows predecessors, not constraints."
        />
        {items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-emerald-500/30">
              <CardContent className="pt-4 space-y-2">
                <p className="text-sm font-semibold text-emerald-400">Ready by dependencies ({ready.length})</p>
                {ready.map((i) => (
                  <p key={i.id} className="text-sm rounded bg-muted/30 px-2 py-1">{i.name}</p>
                ))}
              </CardContent>
            </Card>
            <Card className="border-red-500/30">
              <CardContent className="pt-4 space-y-2">
                <p className="text-sm font-semibold text-red-400">Blocked by predecessors ({blocked.length})</p>
                {blocked.map((i) => (
                  <div key={i.id} className="text-sm rounded bg-muted/30 px-2 py-1">
                    {i.name}
                    {(i.blockers || []).length > 0 && (
                      <span className="block text-xs text-muted-foreground">
                        Waiting on {i.blockers.map((b) => b.predecessor_name).join(", ")}
                      </span>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  if (classified)
    return <SectionError error={classified} onRetry={load} label="What Can Start failed to load" />;

  const instances = view?.instances || [];

  return (
    <div className="space-y-4">
      {/* A `what-can-start-1` response carries no notEstablishedReasons, so a
          withheld front would show an empty panel. Version skew must be
          visible rather than silently losing the lane. */}
      <ContractMismatchCallout
        received={view?.contractVersion}
        supported={WHAT_CAN_START_CONTRACT}
      />

      {/* Realism (C5) */}
      <RealismPanel
        realism={realism}
        loading={realismLoading}
        classified={realismClassified}
        onRetry={load}
      />

      {/* Rollup + actions */}
      <div className="flex flex-wrap items-center gap-2">
        {BUCKET_ORDER.map((status) => {
          const d = STARTABILITY_DISPLAY[status];
          const count = view?.counts?.[status] ?? buckets[status].length;
          if (!count) return null;
          return (
            <Badge key={status} variant="outline" className={`${d.className} font-medium`}>
              <d.Icon className="w-3 h-3 mr-1" />
              {d.label}: {count}
            </Badge>
          );
        })}
        <span className="ml-auto flex items-center gap-2">
          {canEditSchedule(user) && <RecalculateButton onClick={recalculate} busy={recalcBusy} />}
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={load}>
            <RefreshCw className="w-3 h-3 mr-1" />
            Reload
          </Button>
        </span>
      </div>

      {instances.length === 0 ? (
        <SectionEmpty
          icon={Gauge}
          title="No workfaces to classify"
          hint="Link execution instances to this project's activities and the classification will assess them gate by gate."
        />
      ) : (
        BUCKET_ORDER.map((status) => {
          const d = STARTABILITY_DISPLAY[status];
          const list = buckets[status];
          if (list.length === 0) return null;
          return (
            <div key={status} className="space-y-2">
              <h3 className="text-sm font-semibold flex items-center gap-1.5">
                <d.Icon className="w-4 h-4" />
                {d.label}
                <span className="text-muted-foreground font-normal">({list.length})</span>
              </h3>
              {list.map((inst) => (
                <InstanceRow key={inst.instanceId} instance={inst} user={user} />
              ))}
            </div>
          );
        })
      )}

      {/* Safe parallel work */}
      {(view?.safeParallelWork || []).length > 0 && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="py-2.5 px-4 space-y-0.5">
            <p className="text-xs font-semibold text-emerald-400 flex items-center gap-1">
              <Layers className="w-3.5 h-3.5" />
              Safe parallel work — startable now without waiting on anything
            </p>
            <p className="text-xs text-muted-foreground">
              {view.safeParallelWork.map((id) => nameById[id] || id).join(", ")}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Resequencing candidates */}
      {(view?.safeResequencingCandidates || []).length > 0 && (
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardContent className="py-2.5 px-4 space-y-1">
            <p className="text-xs font-semibold text-blue-400 flex items-center gap-1">
              <GitBranch className="w-3.5 h-3.5" />
              Resequencing candidates — the approved sequence is not changed by this report
            </p>
            {view.safeResequencingCandidates.map((c, i) => (
              <p key={i} className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">
                  {c.instanceName || nameById[c.instanceId] || c.instanceId}:
                </span>{" "}
                {c.reason}
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      {(view?.missingInformation || []).length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="py-2.5 px-4 space-y-0.5">
            <p className="text-xs font-semibold text-amber-400 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              The classification cannot establish everything
            </p>
            {view.missingInformation.slice(0, 6).map((m, i) => (
              <p key={i} className="text-xs text-muted-foreground">{m}</p>
            ))}
            {view.missingInformation.length > 6 && (
              <p className="text-[10px] text-muted-foreground">
                +{view.missingInformation.length - 6} more
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* The same classification in the charter's band vocabulary, plus the
          one lane the source read does not carry: what has been RECORDED as
          moving. Below the classification, not above it — it re-expresses
          that answer and never competes with it. */}
      <FlowDirectorPanel payload={flow} error={flowClassified} />
    </div>
  );
};

export default ReadinessTab;
